import camelize from 'camelize';

import store from 'helpers/reduxStore';

import Licenses from './Licenses';

export const MEMBER_PAGE_BOOLEAN_FILTER_KEYS: ReadonlySet<string> = new Set([
  'cal_integration',
  'internal_meetings_recording',
  'external_meetings_recording',
  'google_meet_integration',
  'ms_teams_integration',
  'voiceprint_status'
]);

export const MEMBER_PAGE_STATUS_FILTER_KEY = 'user_status';
export const MEMBER_PAGE_AUTO_RECORDING_FILTER_KEY = 'auto_recording_settings';
export const MEMBER_PAGE_MEETING_PRIVACY_FILTER_KEY = 'meeting_privacy_settings';
export const MEMBER_PAGE_ZOOM_FILTER_KEY = 'zoom_settings';

export const isAdmin = () => {
  const roleInOrganization = store.getState()?.user?.orgRole?.name;

  return roleInOrganization === 'admin';
};

export const isManager = () => {
  const roleInOrganization = store.getState()?.user?.orgRole?.name;

  return roleInOrganization === 'manager';
};

export const isGuest = () => {
  const roleInOrganization = store.getState()?.user?.orgRole?.name;

  return roleInOrganization === 'guest';
};

export const isCurrentUser = (email?: string) =>
  email === store.getState()?.user?.email;

export const isAvoma = () => {
  const orgDomain = store?.getState()?.members?.organization?.domain;

  return ['avoma.com', 'avoma.app'].includes(orgDomain);
};

export const isSSOAccount = (authProvider?: string) =>
  authProvider === 'okta_oidc' || authProvider === 'saml';

export const isSalesforceApiKeyDomain = () => {
  const orgDomain = store?.getState()?.members?.organization?.domain;
  // In case avoma.app does not need that feature
  return ['avoma.com', 'avoma.app', 'appnigma.ai'].includes(orgDomain);
};

export const getIsAvomaDemoAccount = () => {
  const orgDomain = store?.getState()?.members?.organization?.domain;
  // In case we want to implement only for avoma.app demo accounts
  return orgDomain === 'avoma.app';
};

export const getDomainQuarterStartMonth = (domain?: string) =>
  // TODO: Remove hardcoding once backend handles this
  [
    'orumhq.com',
    'rocketlane.com',
    'axionray.com',
    'roboflow.com',
    'blockaid.co',
    'waterworth.net',
    'observe.ai'
  ].includes(domain ?? '')
    ? 1
    : 0;

// input= { a: ['b','c']} --> output = "&a=b&a=c"
export const transformMemberPageFilterQueryParams = (obj: unknown): string => {
  if (typeof obj !== 'object') {
    return '';
  }
  return Object.entries(obj as Record<string, unknown>)
    .map(([key, value]: [string, any]) => {
      if (key === 'sort') {
        const { sortColumn, sortOrder } = value ?? {};
        let result = '';
        if (sortOrder === 'ascending') {
          result = `${sortColumn}`;
        } else if (sortOrder === 'descending') {
          result = `-${sortColumn}`;
        }
        return result ? `&o=${result}` : '';
      }
      if (key === 'searchText') {
        return `&search=${value ?? ''}`;
      }
      if (!Array.isArray(value)) {
        return transformMemberPageFilterQueryParams(value);
      }
      if (key === 'subscription') {
        return (value as string[])
          .map(
            subscriptionName =>
              Licenses.PLANS[
                String(subscriptionName).toLowerCase() as keyof typeof Licenses.PLANS
              ]
          )
          .flat()
          .map(v => `&${key}=${v}`);
      }
      if (MEMBER_PAGE_BOOLEAN_FILTER_KEYS.has(key)) {
        const normalizedBooleanValues = [
          ...new Set((value as unknown[]).map(v => String(v).toLowerCase()))
        ].filter(v => v === 'true' || v === 'false');

        if (normalizedBooleanValues.length !== 1) {
          return '';
        }
        return [`&${key}=${normalizedBooleanValues[0]}`];
      }
      if (key === MEMBER_PAGE_STATUS_FILTER_KEY) {
        const normalizedStatusValues = [...new Set(value as unknown[])]
          .map(v => String(v).toLowerCase().trim())
          .filter(
            v => v === 'active' || v === 'invited' || v === 'deactivated'
          );

        if (normalizedStatusValues.length !== 1) {
          return '';
        }

        const [selectedStatus] = normalizedStatusValues;
        if (selectedStatus === 'active') {
          return ['&active=true', '&invited=false'];
        }
        if (selectedStatus === 'invited') {
          return ['&active=true', '&invited=true'];
        }
        return ['&active=false'];
      }
      if (key === MEMBER_PAGE_AUTO_RECORDING_FILTER_KEY) {
        const normalizedAutoRecordingValues = (value as unknown[]).map(v =>
          String(v).toLowerCase().trim()
        );
        const queryParams: string[] = [];
        const allRecordingStatuses = new Set([
          'host_only',
          'participant_only',
          'host_or_participant',
          'disabled'
        ]);
        const normalizeRecordingStatus = (rawStatus: string) => {
          if (rawStatus === 'true') {
            return 'host_or_participant';
          }
          if (rawStatus === 'false') {
            return 'disabled';
          }
          return allRecordingStatuses.has(rawStatus) ? rawStatus : '';
        };
        const getSelectedRecordingStatuses = (queryKey: string) =>
          [
            ...new Set(
              normalizedAutoRecordingValues
                .filter(v => v.startsWith(`${queryKey}:`))
                .map(v => normalizeRecordingStatus(v.split(':')[1] || ''))
                .filter(Boolean)
            )
          ] as string[];

        ['internal_meetings_recording', 'external_meetings_recording'].forEach(
          queryKey => {
            const selectedStatuses = getSelectedRecordingStatuses(queryKey);
            if (
              selectedStatuses.length === 0 ||
              selectedStatuses.length === allRecordingStatuses.size
            ) {
              return;
            }
            selectedStatuses.forEach(status => {
              queryParams.push(`&${queryKey}=${status}`);
            });
          }
        );

        return queryParams;
      }
      if (key === MEMBER_PAGE_MEETING_PRIVACY_FILTER_KEY) {
        const normalizedPrivacyValues = [
          ...new Set((value as unknown[]).map(v => String(v).toLowerCase().trim()))
        ];
        const privacyOptions = [
          'visibletoorganization',
          'private',
          'team',
          'disabled'
        ];
        const selectedMeetingPrivacy = [
          ...new Set(
            normalizedPrivacyValues
              .map(v => v.split(':')[1] || '')
              .filter(v => privacyOptions.includes(v))
          )
        ];

        return selectedMeetingPrivacy.map(v => `&meeting_privacy=${v}`);
      }
      if (key === MEMBER_PAGE_ZOOM_FILTER_KEY) {
        const normalizedZoomValues = new Set(
          (value as unknown[]).map(v => String(v).toLowerCase().trim())
        );
        const zoomKeyValueMap = [
          ['zoom_cloud_recording', 'zoom_cloud_recording:true', 'zoom_cloud_recording:false'],
          ['zoom_auto_recording', 'zoom_auto_recording:true', 'zoom_auto_recording:false'],
          ['zoom_pmi_recording', 'zoom_pmi_recording:true', 'zoom_pmi_recording:false'],
          ['zoom_auto_delete', 'zoom_auto_delete:true', 'zoom_auto_delete:false'],
          ['zoom_not_using_pmi', 'zoom_not_using_pmi:true', 'zoom_not_using_pmi:false'],
          ['zoom_apps', 'zoom_apps:true', 'zoom_apps:false']
        ] as const;

        return zoomKeyValueMap
          .flatMap(([queryKey, enabledKey, disabledKey]) => {
            const enabled = normalizedZoomValues.has(enabledKey);
            const disabled = normalizedZoomValues.has(disabledKey);
            if (enabled === disabled) {
              return [];
            }
            return [`&${queryKey}=${enabled ? 'true' : 'false'}`];
          });
      }
      return (value as unknown[]).map(v => `&${key}=${v}`);
    })
    .flat()
    .join('');
};

// type getUserTitle = (user: { firstName?: string, lastName?: string } | { first_name?: string, last_name?: string } | null, fallback: string) => string
export const getUserTitle = (
  user:
    | {
        firstName?: string;
        lastName?: string;
        first_name?: string;
        last_name?: string;
        email?: string | null;
      }
    | null
    | undefined,
  fallback = user?.email ?? undefined
) => {
  const { firstName = '', lastName = '' } = camelize(user ?? {});
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName.trim() || fallback || '-';
};
